import { format } from "date-fns";
import { es } from "date-fns/locale/es";
import { Download } from "lucide-react";
import { css } from "styled-system/css";
import { useEffect, useState } from "react";
import { AdminPageShell } from "@/components/admin/admin-page-shell";
import { useAuth } from "@/providers/AuthProvider";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { LoadingFrame } from "@/components/ui/loading-frame";
import { EmptyState } from "@/components/admin/empty-state";
import { buildNotifLogsCsvContent, downloadNotifLogsCsvFile } from "@/lib/notif-logs-csv-export";

type LogRow = {
  id: string;
  channel: string;
  type: string;
  recipient: string;
  status: string;
  error: string | null;
  sentAt: string;
  clientName?: string | null;
  clientPhone?: string | null;
  clientEmail?: string | null;
};

function statusTone(status: string): "success" | "danger" | "warning" | "neutral" {
  const s = status.toLowerCase();
  if (s.includes("ok") || s.includes("sent") || s.includes("delivered")) return "success";
  if (s.includes("fail") || s.includes("error") || s.includes("bounce")) return "danger";
  if (s.includes("pend") || s.includes("queue")) return "warning";
  return "neutral";
}

function channelTone(ch: string): "neutral" | "success" | "warning" {
  const c = ch.toLowerCase();
  if (c.includes("sms") || c.includes("whatsapp") || c.includes("twilio")) return "success";
  if (c.includes("email") || c.includes("sendgrid")) return "warning";
  return "neutral";
}

export function AdminNotifications() {
  const { refresh } = useAuth();
  const [logs, setLogs] = useState<LogRow[] | null>(null);
  const [client, setClient] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [exporting, setExporting] = useState(false);

  function buildFilterQuery() {
    const q = new URLSearchParams();
    if (client.trim()) q.set("client", client.trim());
    if (phone.trim()) q.set("phone", phone.trim());
    if (email.trim()) q.set("email", email.trim());
    if (from) q.set("from", from);
    if (to) q.set("to", to);
    return q;
  }

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const qs = buildFilterQuery().toString();
      const url = qs ? `/api/admin/notif-logs?${qs}` : "/api/admin/notif-logs";
      const r = await fetchWithSessionSync(url, { credentials: "include" }, refresh);
      if (cancelled) return;
      if (!r.ok) return;
      setLogs((await r.json()) as LogRow[]);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh, client, phone, email, from, to]);

  async function exportCsv() {
    setExporting(true);
    try {
      const q = buildFilterQuery();
      q.set("all", "1");
      const r = await fetchWithSessionSync(`/api/admin/notif-logs?${q.toString()}`, { credentials: "include" }, refresh);
      if (!r.ok) return;
      const rows = (await r.json()) as LogRow[];
      const content = buildNotifLogsCsvContent(rows);
      const name = `logs-notificaciones-${format(new Date(), "yyyy-MM-dd-HHmm")}.csv`;
      downloadNotifLogsCsvFile(name, content);
    } finally {
      setExporting(false);
    }
  }

  if (!logs) {
    return (
      <AdminPageShell title="Historial de envíos">
        <LoadingFrame variant="light" minHeight={320} />
      </AdminPageShell>
    );
  }

  return (
    <AdminPageShell
      title="Historial de envíos"
      description="Registro técnico de mensajes enviados (email/WhatsApp) con estado y errores, para soporte y auditoría."
      actions={
        <Button
          type="button"
          variant="secondary"
          onClick={exportCsv}
          disabled={exporting || logs.length === 0}
          className={css({ display: "inline-flex", alignItems: "center", gap: "2" })}
        >
          <Download size={18} strokeWidth={2} aria-hidden />
          {exporting ? "Exportando…" : "Exportar todo"}
        </Button>
      }
    >
      <div
        className={css({
          mb: "4",
          p: "3",
          rounded: "lg",
          borderWidth: "1px",
          borderColor: "border.DEFAULT",
          bg: "bg.card",
          display: "grid",
          gridTemplateColumns: { base: "1fr", md: "repeat(2, minmax(0,1fr))", xl: "repeat(5, minmax(0,1fr))" },
          gap: "2",
        })}
      >
        <input
          value={client}
          onChange={(e) => setClient(e.target.value)}
          placeholder="Cliente"
          className={css({ h: "9", px: "3", rounded: "md", borderWidth: "1px", borderColor: "border.DEFAULT", bg: "white", fontSize: "sm" })}
        />
        <input
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="Teléfono"
          className={css({ h: "9", px: "3", rounded: "md", borderWidth: "1px", borderColor: "border.DEFAULT", bg: "white", fontSize: "sm" })}
        />
        <input
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email"
          className={css({ h: "9", px: "3", rounded: "md", borderWidth: "1px", borderColor: "border.DEFAULT", bg: "white", fontSize: "sm" })}
        />
        <input
          type="date"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          className={css({ h: "9", px: "3", rounded: "md", borderWidth: "1px", borderColor: "border.DEFAULT", bg: "white", fontSize: "sm" })}
        />
        <div className={css({ display: "flex", gap: "2" })}>
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className={css({ h: "9", flex: 1, px: "3", rounded: "md", borderWidth: "1px", borderColor: "border.DEFAULT", bg: "white", fontSize: "sm" })}
          />
          <button
            type="button"
            onClick={() => {
              setClient("");
              setPhone("");
              setEmail("");
              setFrom("");
              setTo("");
            }}
            className={css({ h: "9", px: "3", rounded: "md", borderWidth: "1px", borderColor: "border.DEFAULT", bg: "bg.muted", fontSize: "xs", fontWeight: "semibold", cursor: "pointer" })}
          >
            Limpiar
          </button>
        </div>
      </div>

      {logs.length === 0 ? (
        <EmptyState
          title="Sin envíos registrados"
          description="Cuando el sistema envíe emails o WhatsApp, cada intento quedará listado aquí con canal, destinatario y resultado."
          icon="📨"
        />
      ) : (
        <div className={css({ overflowX: "auto", rounded: "xl", borderWidth: "1px", borderColor: "border.DEFAULT", bg: "bg.card" })}>
          <table className={css({ w: "full", borderCollapse: "collapse", fontSize: "sm" })}>
            <thead>
              <tr className={css({ bg: "bg.muted", textAlign: "left" })}>
                <th className={css({ py: "3", px: "4", fontWeight: "semibold", color: "fg.muted", whiteSpace: "nowrap" })}>Fecha</th>
                <th className={css({ py: "3", px: "4", fontWeight: "semibold", color: "fg.muted" })}>Canal</th>
                <th className={css({ py: "3", px: "4", fontWeight: "semibold", color: "fg.muted" })}>Tipo</th>
                <th className={css({ py: "3", px: "4", fontWeight: "semibold", color: "fg.muted", display: { base: "none", lg: "table-cell" } })}>
                  Cliente
                </th>
                <th className={css({ py: "3", px: "4", fontWeight: "semibold", color: "fg.muted" })}>Destino</th>
                <th className={css({ py: "3", px: "4", fontWeight: "semibold", color: "fg.muted" })}>Estado</th>
                <th className={css({ py: "3", px: "4", fontWeight: "semibold", color: "fg.muted", display: { base: "none", lg: "table-cell" } })}>
                  Detalle
                </th>
              </tr>
            </thead>
            <tbody>
              {logs.map((l) => (
                <tr key={l.id} className={css({ borderTopWidth: "1px", borderColor: "border.DEFAULT", _hover: { bg: "bg.muted" } })}>
                  <td className={css({ py: "3", px: "4", color: "fg.muted", whiteSpace: "nowrap", fontSize: "xs" })}>
                    {format(new Date(l.sentAt), "d MMM yyyy HH:mm", { locale: es })}
                  </td>
                  <td className={css({ py: "3", px: "4" })}>
                    <Badge tone={channelTone(l.channel)}>{l.channel}</Badge>
                  </td>
                  <td className={css({ py: "3", px: "4", fontFamily: "mono", fontSize: "xs" })}>{l.type}</td>
                  <td className={css({ py: "3", px: "4", fontSize: "xs", display: { base: "none", lg: "table-cell" } })}>
                    {l.clientName ?? "—"}
                  </td>
                  <td className={css({ py: "3", px: "4", maxW: "220px", wordBreak: "break-all", fontSize: "xs" })}>{l.recipient}</td>
                  <td className={css({ py: "3", px: "4" })}>
                    <Badge tone={statusTone(l.status)}>{l.status}</Badge>
                  </td>
                  <td className={css({ py: "3", px: "4", fontSize: "xs", color: "red.700", display: { base: "none", lg: "table-cell" } })}>
                    {l.error ?? "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminPageShell>
  );
}
